
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, FileText, Users, DollarSign, Shield } from 'lucide-react';

const PEOShoppingGuide = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-blue-50">
      <Helmet>
        <title>Complete PEO Shopping Guide & Checklist | How to Choose a PEO | The PEO App</title>
        <meta name="description" content="Ultimate guide to shopping for a PEO. Step-by-step checklist, what to look for, questions to ask, and how to avoid common PEO selection mistakes." />
        <link rel="canonical" href="https://thepeoapp.com/peo-shopping-guide" />
        <meta name="keywords" content="PEO shopping guide, how to choose PEO, PEO selection checklist, PEO buying process, PEO comparison guide, what to look for in PEO" />
        
        <meta property="og:title" content="Complete PEO Shopping Guide & Checklist" />
        <meta property="og:description" content="Step-by-step guide to choosing the right PEO for your business. Avoid common mistakes and find the perfect match." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/peo-shopping-guide" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Complete PEO Shopping Guide
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Your step-by-step guide to finding the perfect Professional Employer Organization. 
              Avoid common mistakes and make an informed decision with our comprehensive checklist.
            </p>
          </div>

          <div className="bg-blue-50 p-8 rounded-xl mb-12">
            <h2 className="text-2xl font-bold mb-4">Before You Start Shopping</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">Define Your Needs</h3>
                  <p className="text-gray-700">List your current HR pain points, budget constraints, and must-have services</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">Gather Your Data</h3>
                  <p className="text-gray-700">Employee count, payroll information, current benefit costs, and industry details</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">Set Your Timeline</h3>
                  <p className="text-gray-700">PEO implementations typically take 30-60 days - plan accordingly</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-8">Essential PEO Evaluation Criteria</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <Shield className="h-8 w-8 text-green-600 mb-3" />
                  <h3 className="text-xl font-semibold mb-3">Financial Stability</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• ESAC (Employee Stock Ownership Plan) certification</li>
                    <li>• Years in business and client retention rates</li>
                    <li>• Financial statements and credit ratings</li>
                    <li>• Professional liability insurance coverage</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <Users className="h-8 w-8 text-blue-600 mb-3" />
                  <h3 className="text-xl font-semibold mb-3">Service Capabilities</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• HR support and compliance expertise</li>
                    <li>• Payroll processing and tax management</li>
                    <li>• Benefits administration and options</li>
                    <li>• Workers' compensation management</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <DollarSign className="h-8 w-8 text-purple-600 mb-3" />
                  <h3 className="text-xl font-semibold mb-3">Pricing Structure</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Transparent fee structure (% of payroll vs flat fee)</li>
                    <li>• Setup and implementation costs</li>
                    <li>• Hidden fees and additional charges</li>
                    <li>• Cost comparison with current providers</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <FileText className="h-8 w-8 text-orange-600 mb-3" />
                  <h3 className="text-xl font-semibold mb-3">Technology & Support</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• User-friendly HR management system</li>
                    <li>• Mobile app and employee self-service</li>
                    <li>• Integration with existing systems</li>
                    <li>• Customer support availability and quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-8 rounded-xl mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
              Red Flags to Avoid
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">High-pressure sales tactics or "limited time" offers</span>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Refusal to provide references or client testimonials</span>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Vague or confusing pricing structures</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">No ESAC certification or industry accreditations</span>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Poor online reviews or BBB complaints</span>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Outsourced customer service with no direct access</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Skip the Traditional Shopping Hassle</h2>
            <p className="text-lg text-gray-700 mb-8">
              Instead of calling dozens of PEOs and comparing confusing proposals, 
              use The PEO App to get standardized quotes from pre-vetted providers.
            </p>
            <Link to="/signup">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Start Your Simplified PEO Search
              </Button>
            </Link>
          </div>

          <div className="bg-green-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Questions to Ask Every PEO</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-3">Service & Support</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• What is your client-to-HR-representative ratio?</li>
                  <li>• How do you handle HR compliance updates?</li>
                  <li>• What is your average response time for issues?</li>
                  <li>• Do you have experience in our industry?</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Costs & Contracts</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• What are all the fees involved (setup, admin, etc.)?</li>
                  <li>• How do you handle mid-year rate increases?</li>
                  <li>• What is the contract termination process?</li>
                  <li>• Are there penalties for ending the relationship?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PEOShoppingGuide;
